import type {
  VideoResourceMetadata,
  VideoResourcesMetadata,
} from '@src/types.ts/video';

/**
 * Domain types for the `publish_record` Dato model — one record per publish
 * *target* (a cell of the language × aspectRatio × resolution matrix). See the
 * migration `migrations/2026-07-06-n01_PLAT10010_addPublishRecordModel.ts` for
 * the field contract and `src/repositories/PublishRecordRepository.ts` for the
 * Dato ↔ domain mapping.
 */

export enum PublishStatus {
  Queued = 'queued',
  Rendering = 'rendering',
  Succeeded = 'succeeded',
  Failed = 'failed',
  RetryPending = 'retry_pending',
}

/** Statuses that mean a target is still in flight (drives the seed query). */
export const IN_FLIGHT_PUBLISH_STATUSES: PublishStatus[] = [
  PublishStatus.Queued,
  PublishStatus.Rendering,
  PublishStatus.RetryPending,
];

/**
 * Rendered artifact pointers (resourcesMetadataJson format); `null` until the
 * target succeeds. The download URL is derived from `video.fileKey` on read —
 * never stored. `scenes` / `thumbnail` are added later by the non-blocking
 * scene-detection tail.
 */
export interface PublishOutputJson {
  video: VideoResourceMetadata & {
    bytes?: number;
    width?: number;
    height?: number;
    durationMs?: number;
  };
  scenes?: VideoResourceMetadata;
  thumbnail?: VideoResourceMetadata;
}

/**
 * Provenance pointer to the source state a render was built from — the full
 * resource-pointer map (`videoSourceSnapshot` / `transcriptionSnapshot` /
 * `karaokeData` / `scenes`) of that render's **own AR**. Per-AR, not per-command:
 * a fan-out spans multiple per-AR Video records, so records share this value
 * only with same-AR siblings and differ across ARs. Audit-only today (nothing
 * reads it); retained so it can back a full-snapshot fallback if optimistic
 * retry via `renderSourceKey` ever proves insufficient.
 */
export type PublishSnapshotRef = VideoResourcesMetadata;

/**
 * Per-field SHA-256 staleness hashes for one `(AR × language)` cell. Per-AR
 * fields (`timeline`, `transcript`, `captions`, `scenes`) plus per-language
 * fields (`translation`, `compositionPhrases`) — a stored map (not one combined
 * hash) so `whyOutdated()` can report which category changed.
 */
export interface PublishFingerprints {
  timeline: string;
  transcript: string;
  captions: string;
  translation: string;
  compositionPhrases: string;
  scenes: string;
}

/** One retry attempt in the `attempts` audit array. */
export interface PublishAttempt {
  n: number;
  startedAt: string | null;
  finishedAt: string | null;
  status: PublishStatus | string;
  errorCode?: string | null;
  errorMessage?: string | null;
}

/** A `publish_record` item mapped to camelCase domain form. */
export interface PublishRecord {
  id: string;
  /** Resolved from the `video` link — the per-AR render/mirror/query target. */
  videoId: string;
  /** Resolved from the `story` link — scopes the in-flight seed query. */
  storyId: string;
  groupId: string;
  mirrorsToLegacy: boolean;
  language: string;
  aspectRatio: string;
  resolution: string;
  /** Dato field `publish_status` (`status` is a reserved Dato api_key). */
  publishStatus: PublishStatus;
  renderId: string | null;
  attempts: PublishAttempt[];
  errorCode: string | null;
  errorMessage: string | null;
  renderStartedAt: string | null;
  renderCompletedAt: string | null;
  outputJson: PublishOutputJson | null;
  snapshotRef: PublishSnapshotRef | null;
  fingerprintsJson: PublishFingerprints | null;
  publishedBy: string | null;
  publishedAt: string | null;
  renderSourceKey: string | null;
}

/** A lean live-subscription push: the id plus whatever pipeline-mutable fields
 *  the status query selects, merged onto the full record cached at seed/dispatch. */
export type PublishRecordUpdate = { id: string } & Partial<PublishRecord>;

/** Fields required/allowed when creating a target record. */
export interface CreatePublishRecordInput {
  videoId: string;
  storyId: string;
  groupId: string;
  mirrorsToLegacy: boolean;
  language: string;
  aspectRatio: string;
  resolution: string;
  publishStatus?: PublishStatus;
  snapshotRef?: PublishSnapshotRef | null;
  fingerprintsJson?: PublishFingerprints | null;
  renderSourceKey?: string | null;
  publishedBy?: string | null;
  publishedAt?: string | null;
}

/** Mutable lifecycle fields patched as a render progresses. */
export type UpdatePublishRecordInput = Partial<{
  publishStatus: PublishStatus;
  renderId: string | null;
  attempts: PublishAttempt[];
  errorCode: string | null;
  errorMessage: string | null;
  renderStartedAt: string | null;
  renderCompletedAt: string | null;
  outputJson: PublishOutputJson | null;
  fingerprintsJson: PublishFingerprints | null;
}>;
